import { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { MailCheck, ShieldCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      // Deliberately show the SAME success screen even if the email doesn't exist.
      // This is a security feature — no enumeration.
      setSent(true);
      if (data?.message) toast.success("Check your inbox");
    } catch (e) {
      // Rate-limit or server error — still show a helpful (but generic) message.
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12" data-testid="forgot-password-page">
      <div className="border-2 border-[#09090b] bg-white p-8">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="w-6 h-6 text-[#ea580c]" />
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#ea580c]">Account Recovery</div>
        </div>
        {!sent ? (
          <>
            <h1 className="font-display text-3xl font-black tracking-tight mb-2">Forgot your password?</h1>
            <p className="text-sm text-[#52525b] mb-6">
              Enter the email you used to sign up. We'll send you a secure link (valid for 30 minutes) to set a new password.
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
              <label className="block">
                <div className="text-xs font-bold uppercase tracking-wider text-[#52525b] mb-1.5">Email</div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder="you@example.com"
                  data-testid="forgot-password-email"
                  className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-2.5 outline-none"
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-password-submit"
                className="w-full bg-[#ea580c] hover:bg-[#c2410c] disabled:opacity-60 text-white font-bold py-3"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </form>
            <div className="mt-4 text-xs text-[#71717a] border border-dashed border-[#e4e4e7] p-3 leading-relaxed">
              For your security, this endpoint is rate-limited. If you request too many resets in a short window, you'll need to wait a bit before trying again.
            </div>
          </>
        ) : (
          <div data-testid="forgot-password-sent">
            <div className="flex items-center gap-2 mb-3">
              <MailCheck className="w-6 h-6 text-[#16a34a]" />
              <h1 className="font-display text-2xl font-black tracking-tight">Check your inbox</h1>
            </div>
            <p className="text-sm text-[#52525b] leading-relaxed">
              If an account exists for <b>{email}</b>, a password reset link has just been emailed. The link is valid for <b>30 minutes</b> and can be used only once.
            </p>
            <div className="mt-5 border border-[#e4e4e7] bg-[#fafafa] p-3 text-xs text-[#52525b]">
              Didn't get it? Check your spam/promotions folder. If it still doesn't arrive, try again after a few minutes.
            </div>
          </div>
        )}
        <div className="mt-6 text-sm text-center text-[#52525b]">
          Remembered it? <Link to="/login" className="font-bold text-[#ea580c] hover:underline" data-testid="back-to-login">Back to login</Link>
        </div>
      </div>
    </div>
  );
}
